/** Response from POST /server/files/s3sign */
export type UploadS3SigningData = {
  user_id: string;
  type: string;
  key: string;
  s3_url: string;
  s3_bucket: string;
  s3_region: string;
  presigned_url: string;
  output_bucket: string;
  output_path: string;
  media_convert_data: MediaConvertData;
};

/** Media conversion job configuration for AWS MediaConvert */
export interface MediaConvertData {
  OutputGroups: Array<{
    OutputGroupSettings: {
      FileGroupSettings?: {
        /** S3 destination template with {{output_key}} placeholder, e.g. "s3://bucket/path/{{output_key}}" */
        Destination: string;
      };
      HlsGroupSettings?: {
        /** S3 destination template with {{output_key}} placeholder, e.g. "s3://bucket/path/{{output_key}}" */
        Destination: string;
      };
    };
    Outputs?: Array<{ [key: string]: any }>;
  }>;
  Inputs?: Array<{
    /** Source file location, S3 URL */
    FileInput: string;
    [key: string]: any;
  }>;
}

/** Payload for POST /server/media/converter */
export interface MediaConverterPayload {
  user_id: string;
  /** name without extension  */
  name: string;
  /** extension prefixed with `.`, e.g. `.jpg`  */
  ext: string;
  /** full filename  */
  filename: string;

  /** other file metadata  */
  alt_text?: string | null;
  caption?: string | null;
  decorative?: boolean;
  crop?: CropData;
  subtitles_array?: string[];
  description_array?: string[];
  audio_description?: { _id: string; name: string; url: string } | null;

  /** null in case of disk uploads and camera/mic recordings  */
  external_url: string | null;
  /** upload signing/status information (required)  */
  s3_upload: UploadS3SigningData & { success: boolean };
}

/** Browser S3 upload status returned by signing endpoint */
export interface BrowserS3UploadStatus {
  success: boolean;
  presigned_url: string;
  s3_url: string;
  s3_key: string;
  s3_bucket: string;
  s3_region: string;
  output_path: string;
  output_bucket: string;
  media_convert_data: Record<string, any>;
}


/** Response from the media converter endpoint */
export interface MediaConverterResponse {
  /** False if file failed to upload from frontend and backend */
  success: boolean;

  /** ObjectId of the created media record (db.files or db.images) */
  media_id: string;

  /** Info about uploaded artifact */
  s3_file_data: {
    /** For images key of optimized webp image, otherwise S3 key of the original file */
    key: string;
    /** For images bucket name of optimized webp image, otherwise S3 bucket name of the original file */
    bucket: string;
    /** For images url of optimized webp image, otherwise S3 url of the original file */
    url: string;
    /** For images mime of optimized webp image, otherwise mime type of the original file */
    mimeType: string;

    alt: string;
    caption: string;
    /** image only */
    thumbnail: string | null;

    /** Original file information (only for converted images) */
    original: {
      url: string;
      key: string;
      mimeType: string;
      bucket: string;
    } | null;
  };

  file: MediaLibraryFile | MediaLibraryImage;
}

export type CropData = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Describes `files` property of `/server/files/<courseId>?ext=` */
export type MediaLibraryFile = {
  _id: string;
  created_at: string;
  url: string;
  /** link to our S3 url before AWS transcoding */
  originalUrl?: string;
  name: string;
  mimetype: string;
  /**
   * Array of description files, one element always
   * Format: "{language}::{filename}::{base64_data}"
   */
  description_array?: string[];
  /**
   * Array of subtitle/caption files for video (multiple files, 1 per language) or audio (one file)
   * Format: "{language}::{filename}::{base64_data}"
   */
  subtitles_array?: string[];
  /** Points to an audio file stored in `db.files` collection */
  audio_description?: { _id: string; name: string; url: string };
};

/** Describes `images` property of `/server/images/<courseId>?ext=` */
export type MediaLibraryImage = {
  _id: string;
  created_at: string;
  url: string;
  thumbnailUrl: string;
  name: string;
  /** link to our S3 url before webp optimization */
  original_url?: string;
  /** link to external server from which image was linked (not applicable to disk uploads) */
  external_url?: string;
  label?: string;
  mimetype?: string;
  alt_text?: string;
  caption?: string;
  decorative?: boolean;
};
